import EventIcon from '@mui/icons-material/Event'
import { Button, InputAdornment, Popover, Stack, TextField } from '@mui/material'
import { SxProps, Theme, useTheme } from '@mui/material/styles'
import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    endOfDay,
    endOfMonth,
    endOfWeek,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek
} from 'date-fns'
import { es } from 'date-fns/locale'
import { useState } from 'react'
import { Control, Controller } from 'react-hook-form'
import { DateRangePicker, Range } from 'react-date-range'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

const today = new Date()

const defineds = {
    startOfWeek: startOfWeek(today),
    endOfWeek: endOfWeek(today),
    startOfLastWeek: startOfWeek(addDays(today, -7)),
    endOfLastWeek: endOfWeek(addDays(today, -7)),
    startOfToday: startOfDay(today),
    endOfToday: endOfDay(today),
    startOfYesterday: startOfDay(addDays(today, -1)),
    endOfYesterday: endOfDay(addDays(today, -1)),
    startOfMonth: startOfMonth(today),
    endOfMonth: endOfMonth(today),
    startOfLastMonth: startOfMonth(addMonths(today, -1)),
    endOfLastMonth: endOfMonth(addMonths(today, -1))
}

const staticRangeHandler = {
    range: () => ({
        startDate: new Date(),
        endDate: new Date()
    }),
    isSelected(range: Range) {
        const definedRange = this.range()

        return (
            isSameDay(range.startDate || 0, definedRange.startDate) &&
            isSameDay(range.endDate || 0, definedRange.endDate)
        )
    }
}

const createStaticRanges = (ranges: any[]) => ranges.map(range => ({ ...staticRangeHandler, ...range }))

const staticRanges = createStaticRanges([
    {
        label: 'Hoy',
        range: () => ({
            startDate: defineds.startOfToday,
            endDate: defineds.endOfToday
        })
    },
    {
        label: 'Ayer',
        range: () => ({
            startDate: defineds.startOfYesterday,
            endDate: defineds.endOfYesterday
        })
    },
    {
        label: 'Esta semana',
        range: () => ({
            startDate: defineds.startOfWeek,
            endDate: defineds.endOfWeek
        })
    },
    {
        label: 'Semana pasada',
        range: () => ({
            startDate: defineds.startOfLastWeek,
            endDate: defineds.endOfLastWeek
        })
    },
    {
        label: 'Este mes',
        range: () => ({
            startDate: defineds.startOfMonth,
            endDate: defineds.endOfMonth
        })
    },
    {
        label: 'Mes pasado',
        range: () => ({
            startDate: defineds.startOfLastMonth,
            endDate: defineds.endOfLastMonth
        })
    }
])

const inputRanges = [
    {
        label: 'dias hasta hoy',
        range(value: Range) {
            return {
                startDate: addDays(defineds.startOfToday, (Math.max(Number(value), 1) - 1) * -1),
                endDate: defineds.endOfToday
            }
        },
        getCurrentValue(range: Range) {
            if (!isSameDay(range.endDate || 0, defineds.endOfToday)) return '-'
            if (!range.startDate) return '∞'

            return differenceInCalendarDays(defineds.endOfToday, range.startDate) + 1
        }
    },
    {
        label: 'dias desde hoy',
        range(value: Range) {
            const currentDate = new Date()

            return {
                startDate: currentDate,
                endDate: addDays(currentDate, Math.max(Number(value), 1) - 1)
            }
        },
        getCurrentValue(range: Range) {
            if (!isSameDay(range.startDate || 0, defineds.startOfToday)) return '-'
            if (!range.endDate) return '∞'

            return differenceInCalendarDays(range.endDate, defineds.startOfToday) + 1
        }
    }
]

interface ControlledDateRangePickerProps {
    control: Control<any>
    startDateName: string
    endDateName: string
    label?: string
    disabled?: boolean
    fullWidth?: boolean
    sx?: SxProps<Theme>
}

const formatDate = (date?: Date | string | null) => {
    if (!date) return ''

    const value = typeof date === 'string' ? new Date(date) : date
    if (Number.isNaN(value.getTime())) return ''

    return value.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    })
}

const formatRange = (startDate?: Date | string | null, endDate?: Date | string | null) => {
    const formattedStartDate = formatDate(startDate)
    const formattedEndDate = formatDate(endDate)

    if (!formattedStartDate && !formattedEndDate) return ''
    if (!formattedEndDate) return `${formattedStartDate} -`
    if (!formattedStartDate) return `- ${formattedEndDate}`

    return `${formattedStartDate} - ${formattedEndDate}`
}

export const ControlledDateRangePicker = ({
    control,
    startDateName,
    endDateName,
    label = 'Fecha desde - hasta',
    disabled = false,
    fullWidth = false,
    sx
}: ControlledDateRangePickerProps) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!disabled) setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)

    return (
        <Controller
            control={control}
            name={startDateName}
            render={({ field: { onChange: onStartDateChange, value: valueStart } }) => (
                <Controller
                    control={control}
                    name={endDateName}
                    render={({ field: { onChange: onEndDateChange, value: valueEnd } }) => (
                        <>
                            <TextField
                                label={label}
                                value={formatRange(valueStart, valueEnd)}
                                onClick={handleClick}
                                disabled={disabled}
                                fullWidth={fullWidth}
                                size='small'
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <EventIcon fontSize='small' />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    minWidth: 260,
                                    backgroundColor: 'background.paper',
                                    ...sx
                                }}
                            />
                            <Popover
                                id='date-range-picker-popover'
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                }}
                            >
                                <Stack direction='column' alignItems='flex-end'>
                                    <DateRangePicker
                                        onChange={(item: any) => {
                                            const { startDate, endDate } = item.selection
                                            onStartDateChange(startDate)
                                            onEndDateChange(endDate ? endOfDay(endDate) : undefined)
                                        }}
                                        moveRangeOnFirstSelection={false}
                                        retainEndDateOnFirstSelection={false}
                                        months={2}
                                        ranges={[
                                            {
                                                startDate: valueStart || new Date(),
                                                endDate: valueEnd || new Date(),
                                                key: 'selection',
                                                color: theme.palette.primary.main
                                            }
                                        ]}
                                        direction='horizontal'
                                        preventSnapRefocus
                                        calendarFocus='backwards'
                                        locale={es}
                                        staticRanges={staticRanges}
                                        color={theme.palette.primary.main}
                                        showPreview
                                        inputRanges={inputRanges as any}
                                    />
                                    <Stack direction='row' sx={{ p: 1 }}>
                                        <Button
                                            onClick={() => {
                                                onEndDateChange(undefined)
                                                onStartDateChange(undefined)
                                            }}
                                        >
                                            Limpiar
                                        </Button>
                                        <Button onClick={handleClose}>Cerrar</Button>
                                    </Stack>
                                </Stack>
                            </Popover>
                        </>
                    )}
                />
            )}
        />
    )
}

export default ControlledDateRangePicker
